/**
 * useBestValues Hook
 *
 * Calculates best values for all metrics across funds.
 * Pre-computed once per fund list change to avoid O(n²) calculations.
 */

import { useMemo } from "react";
import type { Fund } from "@curie/shared";
import { calculateBestValues, type BestValues } from "./compare-utils";

/**
 * Hook that calculates and memoizes best values across funds
 *
 * @param funds - Array of funds to compare
 * @returns Object with best value for each metric
 *
 * @example
 * ```tsx
 * function CompareTable({ funds }: { funds: Fund[] }) {
 *   const bestValues = useBestValues(funds);
 *
 *   return (
 *     <tr>
 *       {funds.map(fund => (
 *         <td className={fund.rank === bestValues.rank ? 'text-green-600 font-bold' : ''}>
 *           #{fund.rank}
 *         </td>
 *       ))}
 *     </tr>
 *   );
 * }
 * ```
 */
export function useBestValues(funds: Fund[]): BestValues {
  return useMemo(() => calculateBestValues(funds), [funds]);
}
